import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { cn } from '../lib/utils';
import { 
  HelpCircle, BookOpen, Search, ChevronRight, 
  Home, Users, Calendar, DollarSign, Package, 
  FileText, QrCode, Send, Shield, CreditCard,
  CheckCircle2, Sparkles, Mail
} from 'lucide-react';

const gettingStartedSteps = [
  {
    title: "Add Your First Horse",
    description: "Go to Horses page and click 'Add Horse'. Enter your horse's details like name, breed, and date of birth.",
    icon: "🐴",
    link: "/horses"
  },
  {
    title: "Set Up Rider Profiles",
    description: "Add yourself and any other riders who work with your horses. These appear in ride log dropdowns and can be linked to QR emergency contacts.",
    icon: "👤",
    link: "/rider-profiles"
  },
  {
    title: "Set Up Health Records",
    description: "Track vaccinations, worming, farrier visits, and vet checks. The app will remind you when they're due.",
    icon: "💉",
    link: "/horses"
  },
  {
    title: "Log Your Rides",
    description: "Keep a training diary by logging rides with duration, type, and notes. Select the rider from your profiles.",
    icon: "🏇",
    link: "/horses"
  },
  {
    title: "Set Up QR Emergency Info",
    description: "Each horse gets a unique QR code. Add emergency contact details and link a rider's phone number for quick access.",
    icon: "📱",
    link: "/horses"
  },
  {
    title: "Organize Your Tack",
    description: "Add your saddles, bridles, and gear to Tack Vault. Assign items to horses or riders.",
    icon: "🎠",
    link: "/gear"
  },
  {
    title: "Track Expenses",
    description: "Log all horse-related costs in Finance Vault. Generate reports for tax time or budgeting.",
    icon: "💰",
    link: "/expenses"
  },
  {
    title: "Invite Your Team",
    description: "Go to the Team page in the sidebar. Enter an email address to invite helpers. They'll get an email and can join once they sign up or log in.",
    icon: "👥",
    link: "/team"
  },
  {
    title: "Switch Between Accounts",
    description: "If you're a team member AND have your own horses, look for the Account Switcher dropdown at the top of the sidebar. It shows your current account name - click to switch between 'Your Name's Account' and team accounts you belong to.",
    icon: "🔄",
    link: null,
    isInfoOnly: true
  }
];

const faqItems = [
  {
    category: "Getting Started",
    questions: [
      {
        q: "How do I add a new horse?",
        a: "Go to the Horses page and click the 'Add Horse' button. Fill in your horse's details and click Save. You can always edit these details later."
      },
      {
        q: "What's the difference between horse tabs (In Work, Spelling, etc.)?",
        a: "These help organize your horses by their current status: In Work (active training), Spelling (on a break or retired), Breeding (breeding stock), Youngstock (young horses not yet in work), and Former (sold or deceased horses)."
      },
      {
        q: "How do I move a horse to a different tab?",
        a: "Open the horse's profile and change the 'Category' dropdown to the desired status. The horse will move to that tab."
      }
    ]
  },
  {
    category: "History Timeline",
    questions: [
      {
        q: "What is the History Timeline?",
        a: "The History tab on each horse's profile shows a chronological timeline of significant life events. This creates a complete provenance record that follows the horse through ownership changes, providing future owners with valuable background information."
      },
      {
        q: "What milestone events can I add?",
        a: "You can record these milestone events:\n\n• **New Owner** - Document ownership changes with owner name and date\n• **International Travel** - Record import/export with country and travel date\n• **Gelding** - Mark when a colt was gelded\n• **Broken In** - Document when the horse was started under saddle, including trainer name\n• **Training** - Record professional training periods with trainer details\n• **Foaling** - Log when a mare has foaled, including sire, foal name, sex, and outcome\n• **Other** - Any custom milestone with your own description"
      },
      {
        q: "How do I add a history event?",
        a: "Go to the horse's profile and click on the 'History' tab. Click 'Add Event', select the event type, fill in the relevant details, and save. Events are displayed in chronological order on the timeline."
      },
      {
        q: "What details can I add for foaling events?",
        a: "Foaling events capture comprehensive breeding information:\n\n• Sire of the foal\n• Foal's name\n• Outcome (live birth or stillborn)\n• For live foals: sex (colt, filly), single or twin, and delivery type (normal or assisted)"
      },
      {
        q: "What happens to history when I transfer a horse?",
        a: "All history events transfer with the horse and become read-only for the new owner. This preserves the complete provenance record. A 'New Owner' event is automatically added when the transfer is accepted, documenting the ownership change."
      },
      {
        q: "Can I edit history events from previous owners?",
        a: "No. Events created by previous owners are marked as read-only (shown with a lock icon) to maintain historical integrity. You can only edit events that you've added during your ownership period."
      }
    ]
  },
  {
    category: "Mobile App",
    questions: [
      {
        q: "Can I install EquiVault as an app on my phone?",
        a: "Yes! EquiVault is a Progressive Web App (PWA) that can be installed directly to your phone - no app store needed. Once installed, it works just like a regular app with its own icon on your home screen or app drawer."
      },
      {
        q: "How do I install EquiVault on Android?",
        a: "**Option 1 - Automatic prompt:**\nWhen you first visit EquiVault, you may see an 'Install EquiVault' banner at the bottom. Tap 'Install' to add it to your apps.\n\n**Option 2 - Manual install:**\n1. Open EquiVault in Chrome\n2. Tap the three dots menu (⋮) in the top right\n3. Tap 'Install app' or 'Add to Home screen'\n4. Tap 'Install' to confirm\n\nThe app will install to your app drawer just like any other app!"
      },
      {
        q: "How do I install EquiVault on iPhone/iPad?",
        a: "1. Open EquiVault in Safari (must use Safari)\n2. Tap the Share button (square with arrow) at the bottom\n3. Scroll down and tap 'Add to Home Screen'\n4. Tap 'Add' in the top right\n\nThe app icon will appear on your home screen!"
      },
      {
        q: "What's the difference between the website and installed app?",
        a: "The installed app provides a better mobile experience:\n\n• **Full screen** - No browser address bar taking up space\n• **Quick access** - Launch from your home screen or app drawer\n• **App switcher** - Shows as a separate app, not a browser tab\n• **Faster loading** - Core files are cached on your device\n\nThe features and data are identical - it's the same EquiVault, just optimized for mobile use."
      },
      {
        q: "Will my data sync across devices?",
        a: "Yes! Your data is stored securely in the cloud. Log in on any device - phone, tablet, or computer - and all your horses and records will be there."
      },
      {
        q: "Does the app work offline?",
        a: "Basic pages may load from cache when offline, but most features require an internet connection to sync your data. You'll see an offline indicator at the top of the screen if you lose connection."
      }
    ]
  },
  {
    category: "Health & Reminders",
    questions: [
      {
        q: "How do health reminders work?",
        a: "When you log a health record (like worming or farrier), you can set a 'Next Due Date'. Reminders are generated based on this date and appear on your dashboard when they're coming up. You control when each reminder is due - there are no default intervals."
      },
      {
        q: "How do I set an appointment reminder?",
        a: "Go to the Reminders page and click 'Add Reminder'. Select 'Appointment' as the type, then choose the appointment category (Veterinary, Farrier, Dentist, or Other). Enter the date and time, and optionally assign it to a specific horse or keep it as a general reminder. Appointments will show as 'Appointment - [Type]' in your upcoming reminders."
      },
      {
        q: "Can I customize reminder intervals?",
        a: "Absolutely! When adding a health record, simply set the 'Next Due Date' to whenever you want to be reminded. Each horse and record type can have different intervals based on your preferences or vet recommendations."
      },
      {
        q: "Where do I see all my reminders?",
        a: "Check the Reminders page in the sidebar, or view the 'Upcoming Care' section on your Dashboard homepage. The dashboard shows up to 10 reminders due within the next 30 days."
      },
      {
        q: "Can I edit or delete reminders?",
        a: "Yes! Click on any reminder in the Reminders page to edit its details (type, date, notes). You can also edit general reminders from the home page by clicking on them in the Upcoming Care section."
      }
    ]
  },
  {
    category: "Subscription & Billing",
    questions: [
      {
        q: "Why are prices shown in NZD?",
        a: "EquiVault is a New Zealand-based company, so our prices are listed in **New Zealand Dollars (NZD)**. However, **Stripe handles automatic currency conversion** at checkout - you'll pay in your local currency (USD, GBP, EUR, AUD, etc.) at the current exchange rate.\n\n**Example:** If you're in the US, the $8.99 NZD Rider plan converts to approximately **$5.50 USD** (rates vary). You'll see the exact amount in your currency before completing payment."
      },
      {
        q: "How do I subscribe to a plan?",
        a: "1. Tap **Account** in the sidebar menu\n2. Scroll down to the **'Choose Your Plan'** section\n3. Select **Monthly** or **Yearly** billing using the toggle\n4. Choose your plan (Rider or Pro) and tap the button to proceed to checkout\n\n**Tip:** On mobile, you may need to scroll down past your profile information to see the subscription options."
      },
      {
        q: "Can I pay yearly instead of monthly?",
        a: "Yes! We offer yearly subscriptions with a **20% discount**:\n\n• **Rider:** $8.99/month or **$86/year** (~$7.17/mo)\n• **Pro:** $14.99/month or **$144/year** (~$12/mo)\n\nTo choose yearly billing, go to Account → scroll to 'Choose Your Plan' → tap the **'Yearly'** toggle before selecting your plan."
      },
      {
        q: "What's included in the free trial?",
        a: "Your 14-day free trial includes full access to all features - unlimited horses, all logs and records, reports, and more. No subscription obligation or credit card details required to use EquiVault during the trial period. Simply sign up and start using the app right away."
      },
      {
        q: "What happens when my trial ends?",
        a: "Your account switches to read-only mode. You can still view all your data, but you'll need to subscribe to add or edit records."
      },
      {
        q: "What's the difference between Rider and Pro plans?",
        a: "**Rider** ($8.99/mo or $86/yr) supports up to 5 horses and 1 helper - perfect for private owners.\n\n**Pro** ($14.99/mo or $144/yr) offers unlimited horses and team members, plus the Stable Dashboard - ideal for trainers, breeders, and stables."
      },
      {
        q: "How do I manage or cancel my subscription?",
        a: "Go to Account page and click 'Manage Subscription'. This opens Stripe's billing portal where you can:\n\n• Cancel your subscription\n• Update payment methods\n• View invoices and receipts\n• Switch between monthly and yearly billing"
      }
    ]
  },
  {
    category: "Team & Sharing",
    questions: [
      {
        q: "How do I invite someone to help manage my horses?",
        a: "Go to the Team page in the sidebar. Enter their email address and click 'Send Invite'. They'll receive an email with instructions. Once they sign up or log in with that email, they'll automatically have access to your horses."
      },
      {
        q: "What happens when I send an invite?",
        a: "The person receives an email inviting them to EquiVault. If they're new, they create an account using that email. If they already have an account, they just log in. Either way, they're automatically added to your team and can see your horses."
      },
      {
        q: "What can team members do?",
        a: "Team members can view and manage all your horses - add rides, health records, expenses, upload photos, etc. They cannot manage your subscription or invite other users."
      },
      {
        q: "Can I remove someone from my team?",
        a: "Yes, go to the Team page in the sidebar and click the 'Remove Access' button next to their name. You'll be asked to confirm, then they'll immediately lose access to your horses (but keep their own personal account)."
      },
      {
        q: "How many team members can I have?",
        a: "Rider plan ($8.99/mo): Invite 1 helper. Pro plan ($14.99/mo): Invite unlimited team members. Trial users can also test the team feature."
      },
      {
        q: "I'm a team member AND have my own horses - how do I see both?",
        a: "Use the Account Switcher! If you have your own horses and are also a team member of another stable, you'll see a dropdown button in the sidebar. Click it to switch between 'My Personal Account' and the team account(s) you belong to."
      },
      {
        q: "How does the Account Switcher work?",
        a: "When you're a member of multiple accounts, a dropdown appears in your sidebar showing all accounts you can access. Click to switch between them - each account shows only its own horses, expenses, and records. This is perfect for stable staff who also have personal horses!"
      }
    ]
  },
  {
    category: "Multiple Accounts & Switching",
    questions: [
      {
        q: "Can I have my own horses AND help manage someone else's stable?",
        a: "Yes! You can have your own personal EquiVault account with your horses, AND be invited as a team member to manage another person's stable. Both are completely separate."
      },
      {
        q: "How do I switch between my personal account and a team account?",
        a: "Look for the Account Switcher dropdown at the top of the sidebar. Click it to see all accounts you have access to, then select the one you want to view. The page will refresh to show that account's data."
      },
      {
        q: "Will my personal horses mix with the team's horses?",
        a: "No, they're completely separate. When you're viewing your personal account, you only see your horses. When you switch to a team account, you only see that stable's horses. Data never mixes between accounts."
      },
      {
        q: "I don't see the Account Switcher - where is it?",
        a: "The Account Switcher only appears if you have access to multiple accounts (your own + at least one team). If you only have one account, it won't show. Ask a stable owner to invite you to their team to enable this feature."
      },
      {
        q: "As a team member, what can I do on someone else's account?",
        a: "You can view and manage all their horses - add rides, health records, expenses, upload photos, and more. You cannot manage their subscription, billing, or invite other team members."
      }
    ]
  },
  {
    category: "QR Codes & Transfers",
    questions: [
      {
        q: "What is the QR-ID feature?",
        a: "Each horse gets a unique QR code that links to a public profile page. This is great for emergencies - anyone who scans it can see your contact info and the horse's key details."
      },
      {
        q: "How do I transfer a horse to a new owner?",
        a: "Open the horse's profile and click the Transfer button. Enter the new owner's email. They'll receive a notification to accept the transfer."
      },
      {
        q: "What data transfers with the horse?",
        a: "History, feed entries, health records, event logs, documents, and photos transfer as read-only records. Training logs, expenses, and tack assignments start fresh for the new owner."
      }
    ]
  },
  {
    category: "Agents & Trainers",
    questions: [
      {
        q: "Should I transfer my horse's file when sending them to a trainer?",
        a: "It depends on the arrangement. For short-term training (weeks or a few months), you may prefer to keep the file and update notable events yourself. For longer training periods or sales on behalf, transferring the file gives the trainer full management access."
      },
      {
        q: "How does transferring to a trainer work?",
        a: "Transfer the horse file to the trainer's EquiVault account. The trainer receives the horse with all its history intact (marked as read-only). They should then edit the auto-generated 'New Owner' event and change it to a 'Training' event - this documents that the horse is with them for training, not as a new owner."
      },
      {
        q: "What happens when training is complete?",
        a: "If the horse is sold: The trainer transfers the file to the new buyer. If returning to you: The trainer transfers the file back to your account. Either way, the full training history is preserved and transfers with the horse."
      },
      {
        q: "I'm a trainer selling horses on behalf - what's the workflow?",
        a: "1) Owner transfers horse file to you. 2) Edit the 'New Owner' event to 'Training' with your details. 3) Manage the horse during your care period. 4) When sold, transfer to the buyer. The buyer receives full history showing previous owner → your training period → their ownership."
      },
      {
        q: "Can previous owners see changes I make?",
        a: "No. Once transferred, the previous owner no longer has access to the horse file. However, all events from their ownership period are preserved as read-only records in the horse's history - providing complete provenance for future owners."
      }
    ]
  },
  {
    category: "Reports & Documents",
    questions: [
      {
        q: "How do I generate a report?",
        a: "Go to Reports page, choose the report type (Horse, Tack, or Expense), select your filters, and click Generate. Reports are saved to your vault for future access."
      },
      {
        q: "Can I download reports as PDF?",
        a: "Yes! After generating a report, click the Download button to save it as a PDF to your device."
      },
      {
        q: "Where are my uploaded documents stored?",
        a: "Documents are organized in two places depending on how you create them:\n\n• **General Documents** (no horse assigned): Created from the main Document Vault page with 'General (no horse)' selected. These appear in the Document Vault grouped under 'General'.\n\n• **Horse-Specific Documents**: Created either from the Document Vault with a horse selected, or from a horse's profile Documents tab. These appear both in the main Document Vault (grouped by horse) and in that horse's Documents tab.\n\nYou can change a document's horse assignment anytime by editing it."
      },
      {
        q: "What's the difference between the Document Vault and a horse's Documents tab?",
        a: "The **Document Vault** (main Documents page) shows ALL your documents - both general and horse-assigned - grouped by category. A **horse's Documents tab** only shows documents specifically assigned to that horse. Both views let you add, edit, and delete documents."
      }
    ]
  },
  {
    category: "Troubleshooting",
    questions: [
      {
        q: "Data isn't syncing - I can't see changes made by team members",
        a: "If you're not seeing recent changes (like rides logged by team members), your device may have cached old data. To fix this:\n\n1. Go to **Account** (via the More menu on mobile)\n2. Scroll down to the **Troubleshooting** section\n3. Tap **'Clear Cache & Reload'**\n4. Log back in\n\nThis clears your device's cached data and forces a fresh sync from the server."
      },
      {
        q: "When should I use 'Clear Cache & Reload'?",
        a: "Use this feature when:\n\n• **Data seems out of sync** - You don't see changes made by team members, or they don't see yours\n• **Pages show old information** - Dashboard or horse profiles show outdated data even after refreshing\n• **After switching between accounts** - If data seems mixed up between your personal and team accounts\n• **App behaving strangely** - Unexpected errors or features not working as expected\n\nClearing the cache is safe and won't delete any of your data - it just refreshes your local copy."
      },
      {
        q: "What does 'Clear Cache & Reload' actually do?",
        a: "This button:\n\n• Clears your browser's stored data for EquiVault\n• Removes any cached pages or responses\n• Resets the app to a fresh state\n• Logs you out so you can log back in with fresh data\n\n**Your actual data is safe** - all your horses, health records, rides, and documents are stored securely in the cloud. Clearing the cache just removes the local copy on your device."
      },
      {
        q: "The app looks different on my phone vs computer",
        a: "EquiVault is designed to adapt to different screen sizes. On phones, you'll see a bottom navigation bar with a 'More' menu for additional options. On tablets and computers, you'll see a full sidebar. The features are identical - just laid out differently for the best experience on each device."
      },
      {
        q: "I'm getting errors when trying to log in or register",
        a: "If you're having trouble logging in, especially on iPhone/Safari:\n\n1. Make sure you have a stable internet connection\n2. Try clearing your browser cache (Settings → Safari → Clear History and Website Data)\n3. If using the installed app, try uninstalling and reinstalling it\n4. Try using a different browser (Chrome often works better)\n\nIf problems persist, contact support with details about your device and browser."
      }
    ]
  }
];

const featureHighlights = [
  { icon: Home, title: "Dashboard", description: "Your home base with reminders, recent activity, and quick-add buttons" },
  { icon: Users, title: "Rider Profiles", description: "Manage riders who work with your horses" },
  { icon: Calendar, title: "Event Logs", description: "Track competitions, shows, and important dates" },
  { icon: DollarSign, title: "Finance Vault", description: "Log expenses and generate financial reports" },
  { icon: Package, title: "Tack Vault", description: "Inventory all your gear and equipment" },
  { icon: FileText, title: "Documents", description: "Store registrations, vet reports, and contracts" },
  { icon: QrCode, title: "QR-ID Profiles", description: "Emergency contact info via scannable QR code" },
  { icon: Send, title: "Horse Transfers", description: "Safely transfer ownership with full history" },
];

export const HelpPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');
  const navigate = useNavigate();

  const filteredFAQ = faqItems.map(category => ({
    ...category,
    questions: category.questions.filter(
      item => 
        item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl" data-testid="help-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-forest" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Help & Support</h1>
            <p className="text-muted-foreground">Everything you need to get the most out of EquiVault</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search help topics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="help-search"
          />
        </div>

        {/* Quick Navigation */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            variant={activeSection === 'getting-started' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection('getting-started')}
            className={activeSection === 'getting-started' ? 'bg-forest hover:bg-forest-dark' : ''}
          >
            <BookOpen className="w-4 h-4 mr-1" />
            Getting Started
          </Button>
          <Button
            variant={activeSection === 'faq' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection('faq')}
            className={activeSection === 'faq' ? 'bg-forest hover:bg-forest-dark' : ''}
          >
            <HelpCircle className="w-4 h-4 mr-1" />
            FAQ
          </Button>
          <Button
            variant={activeSection === 'features' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection('features')}
            className={activeSection === 'features' ? 'bg-forest hover:bg-forest-dark' : ''}
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Features
          </Button>
        </div>

        {/* Getting Started Section */}
        {activeSection === 'getting-started' && !searchTerm && (
          <Card className="border-0 shadow-card bg-white mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-forest" />
                Getting Started Guide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-6">
                Welcome to EquiVault! Follow these steps to set up your horse management system.
              </p>
              <div className="space-y-4">
                {gettingStartedSteps.map((step, index) => (
                  <div 
                    key={index}
                    onClick={() => step.link && navigate(step.link)}
                    className={cn(
                      "flex items-start gap-4 p-4 rounded-lg transition-colors",
                      step.link 
                        ? "bg-muted/30 hover:bg-muted/50 cursor-pointer group" 
                        : "bg-forest/5 border border-forest/20"
                    )}
                    role={step.link ? "button" : undefined}
                    tabIndex={step.link ? 0 : undefined}
                    onKeyDown={(e) => e.key === 'Enter' && step.link && navigate(step.link)}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center text-xl">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">Step {index + 1}</Badge>
                        <h3 className="font-semibold">{step.title}</h3>
                        {step.isInfoOnly && (
                          <Badge className="bg-forest/10 text-forest text-xs">Info</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                    </div>
                    {step.link && (
                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 group-hover:text-forest group-hover:translate-x-1 transition-all" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* FAQ Section */}
        {(activeSection === 'faq' || searchTerm) && (
          <Card className="border-0 shadow-card bg-white mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-forest" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredFAQ.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No results found for "{searchTerm}"</p>
                  <Button 
                    variant="link" 
                    onClick={() => setSearchTerm('')}
                    className="mt-2"
                  >
                    Clear search
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredFAQ.map((category, categoryIndex) => (
                    <div key={categoryIndex}>
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
                        {category.category}
                      </h3>
                      <Accordion type="single" collapsible className="space-y-2">
                        {category.questions.map((item, itemIndex) => (
                          <AccordionItem 
                            key={itemIndex} 
                            value={`${categoryIndex}-${itemIndex}`}
                            className="border rounded-lg px-4"
                          >
                            <AccordionTrigger className="text-left hover:no-underline">
                              {item.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground">
                              {item.a}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Features Section */}
        {activeSection === 'features' && !searchTerm && (
          <Card className="border-0 shadow-card bg-white mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-forest" />
                Feature Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {featureHighlights.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-4 rounded-lg bg-muted/30"
                  >
                    <div className="w-10 h-10 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-forest" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Support */}
        <Card className="border-0 shadow-card bg-white">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-saddle/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-saddle" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Need More Help?</h3>
                <p className="text-sm text-muted-foreground">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
              </div>
              <a href="mailto:support@equivault.app">
                <Button className="gap-2 bg-saddle hover:bg-saddle/90">
                  <Mail className="w-4 h-4" />
                  Email Support
                </Button>
              </a>
            </div>
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground text-center">
                Email us at <a href="mailto:support@equivault.app" className="text-forest font-medium hover:underline">support@equivault.app</a> and we'll get back to you as soon as possible.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
